// Delete file when cleaning up jira_list_nin_merge

import { useEffect, useMemo, useRef } from 'react';
import { useIntl } from '@atlassian/jira-intl';
import { useIsActiveRoute } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import { useFlagsService } from '@atlassian/jira-flags';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import messages from './messages.tsx';

type Tab = {
	id: string | null | undefined;
	typeKey: string | null | undefined;
	appId: string | null | undefined;
	dataId: string | null | undefined;
};

type NINListMergeListenerProps = {
	tabs?: Tab[];
};

const PROJECT_NIN_ROUTES = new Set([
	'issue-navigator-core', // Business NIN
	'software-issue-navigator', // TMP Software NIN
	'issue-navigator-classic', // CMP Software NIN
]);

export const NINListMergeObserver = ({ tabs }: NINListMergeListenerProps) => {
	const hasAllWorkTab = useMemo(() => tabs?.some((tab) => tab.typeKey === 'ISSUES'), [tabs]);
	const hasAllWorkTabRef = useRef(hasAllWorkTab);
	const hasShownFlagRef = useRef(false);

	const isProjectNINRoute = useIsActiveRoute(PROJECT_NIN_ROUTES);
	const { showFlag } = useFlagsService();
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		// If the frontend already has FG jira_list_nin_merge as ON, then we don't need to do anything here
		if (fg('jira_list_nin_merge') || !isProjectNINRoute || hasShownFlagRef.current) {
			return;
		}

		// If the All Work tab has disappeared, it means the NIN/List merge FG
		// has been turned ON on the server side, and the client is now out of sync
		if (hasAllWorkTabRef.current && !hasAllWorkTab && typeof window !== 'undefined') {
			showFlag({
				messageType: 'transactional',
				messageId: 'horizontal-nav-tabs.ui.show-flag.error.nin-list-merge-reload-page-notification',
				type: 'error',
				title: formatMessage(messages.allWorkTabRemovedTitle),
				description: formatMessage(messages.allWorkTabRemovedDescription),
				actions: [
					{
						content: formatMessage(messages.allWorkTabRemovedButton),
						onClick: () => window.location.reload(),
					},
				],
			});

			fireUIAnalytics(createAnalyticsEvent({}), 'allWorkNavigationTab removed', {});
			hasShownFlagRef.current = true;
		}

		hasAllWorkTabRef.current = hasAllWorkTab;
	}, [createAnalyticsEvent, formatMessage, hasAllWorkTab, isProjectNINRoute, showFlag]);

	return null;
};
